import React from 'react';
import {
  Body,
  Button,
  Flex,
  Input,
  OptionType,
  Select,
} from '@cognite/cogs.js';
import { Step } from 'components/DrawerSteps/DrawerSteps';
import { LabelListEdit } from 'components/LabelList/LabelListEdit';
import { LabelListDisplay } from 'components/LabelList/LabelListDisplay';
import { Spinner } from 'components/Spinner/Spinner';
import { ConditionItem } from '../components/Schemas/ViewSchemaPage/ProjectRuleset/components/Conditions/ConditionItem';
import { LogicalRule } from 'utils/rules';
import { SchemaWithTypes } from './useSchemaData';
import {
  StyledTextarea,
  SelectWrapper,
  HintText,
  WarningPanel,
  InfoPanel,
} from './DataConsistencyDrawer.styles';

export type SchemaFilterStepsParams = {
  creating: boolean;
  nameInput: string;
  setNameInput: (v: string) => void;
  descriptionInput: string;
  setDescriptionInput: (v: string) => void;
  labelListInput: string[];
  setLabelListInput: (labels: string[]) => void;
  selectedSchemaName: string;
  selectedSchemaType: string;
  setSelectedSchemaType: (v: string) => void;
  filtersTree: LogicalRule;
  setFiltersTree: (value: LogicalRule) => void;
  schemas: SchemaWithTypes[];
  loadingSchemas: boolean;
  selectedSchema: SchemaWithTypes | undefined;
  hasInvalidName: boolean;
  hasInvalidSchema: boolean;
  hasInvalidSchemaType: boolean;
  nextStep: VoidFunction;
  handleSchemaChange: (schemaName: string) => void;
};

type BuildSchemaFilterStepsParams = SchemaFilterStepsParams;

const fallbackDescription = 'No details';

const renderSchemaTypeContent = (params: {
  selectedSchema: SchemaWithTypes | undefined;
  selectedSchemaType: string;
  setSelectedSchemaType: (value: string) => void;
  hasInvalidSchemaType: boolean;
  nextStep: VoidFunction;
}) => {
  const {
    selectedSchema,
    selectedSchemaType,
    setSelectedSchemaType,
    hasInvalidSchemaType,
    nextStep,
  } = params;

  const hasTypes = selectedSchema && selectedSchema.types.length > 0;
  const schemaError = selectedSchema?.error;

  return (
    <Flex direction="column" gap={16}>
      <Body size="medium">
        Select the primary schema type. This identifies the main collection, but
        filters can reference attributes across multiple related types.
      </Body>

      {hasTypes ? (
        <SelectWrapper>
          <Select
            fullWidth
            closeMenuOnSelect
            value={
              selectedSchemaType
                ? { label: selectedSchemaType, value: selectedSchemaType }
                : undefined
            }
            placeholder="Select schema type"
            onChange={(option: OptionType<string>) =>
              setSelectedSchemaType(option?.value ?? '')
            }
            options={
              selectedSchema?.types.map((t) => ({
                label: t.name,
                value: t.name,
              })) || []
            }
          />
        </SelectWrapper>
      ) : schemaError ? (
        <WarningPanel>
          <Body size="medium" strong>
            Schema Configuration Required
          </Body>
          <Body size="small">{schemaError.message}</Body>
          <Body size="small">
            <strong>Action Required:</strong> Please ensure the schema is
            properly configured with a GraphQL definition in the data source.
            You can view and manage schemas in the main Schemas tab.
          </Body>
        </WarningPanel>
      ) : (
        <InfoPanel>
          <Body size="small">
            No schema types available. Please select a different schema or
            configure this schema in the data source.
          </Body>
        </InfoPanel>
      )}
      <Button type="primary" disabled={hasInvalidSchemaType} onClick={nextStep}>
        Next
      </Button>
    </Flex>
  );
};

const renderFiltersContent = (params: {
  filtersTree: LogicalRule;
  setFiltersTree: (value: LogicalRule) => void;
  selectedSchema: SchemaWithTypes | undefined;
  selectedSchemaType: string;
}) => {
  const { filtersTree, setFiltersTree, selectedSchema, selectedSchemaType } =
    params;
  const allSchemaTypes = selectedSchema?.types || [];

  return (
    <Flex direction="column" gap={16}>
      <Body size="medium">
        Define filters to create a subset of data. Add one or more conditions to
        filter instances based on attribute values.
      </Body>

      <ConditionItem
        condition={filtersTree}
        schemas={allSchemaTypes}
        root={filtersTree}
        ancestors={[]}
        onRootUpdate={(newTree: LogicalRule) => setFiltersTree(newTree)}
        selectedSchema={selectedSchema?.types?.find(
          (t) => t.name === selectedSchemaType
        )}
        enabledPropertyComparison={false}
        showLabels={false}
        isFilter={true}
      />

      <HintText size="small">
        Leave empty to include all instances. You can use AND/OR logic and nest
        conditions in groups for complex filtering.
      </HintText>
    </Flex>
  );
};

export const buildSchemaFilterSteps = (
  params: BuildSchemaFilterStepsParams
): Step[] => {
  const {
    creating,
    nameInput,
    setNameInput,
    descriptionInput,
    setDescriptionInput,
    labelListInput,
    setLabelListInput,
    selectedSchemaName,
    selectedSchemaType,
    setSelectedSchemaType,
    filtersTree,
    setFiltersTree,
    schemas,
    loadingSchemas,
    selectedSchema,
    hasInvalidName,
    hasInvalidSchema,
    hasInvalidSchemaType,
    nextStep,
    handleSchemaChange,
  } = params;

  const hasFilters =
    filtersTree.children.length > 0 &&
    filtersTree.children.some((c) =>
      c._type === 'RelationalRule' ? c.property !== '' : true
    );

  return [
    {
      key: 'description',
      title: 'General information',
      subTitle: '',
      description:
        nameInput || descriptionInput ? (
          <>
            {nameInput && (
              <Body size="medium" strong>
                {nameInput}
              </Body>
            )}
            {descriptionInput && <Body size="medium">{descriptionInput}</Body>}
          </>
        ) : (
          fallbackDescription
        ),
      content: (
        <Flex direction="column" gap={16}>
          <Body size="medium">
            The general information will be displayed in the schema filter
            overview.
          </Body>
          <Input
            fullWidth
            placeholder="Name"
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
          />
          <StyledTextarea
            placeholder="Description"
            value={descriptionInput}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setDescriptionInput(e.target.value)
            }
          />
          <Button type="primary" disabled={hasInvalidName} onClick={nextStep}>
            Next
          </Button>
        </Flex>
      ),
      invalid: hasInvalidName,
    },
    {
      key: 'labels',
      title: 'Labels',
      subTitle: '',
      description: labelListInput.length ? (
        <LabelListDisplay
          labels={labelListInput.map((label) => ({
            text: label,
            key: label,
          }))}
          limitLabels={7}
        />
      ) : (
        fallbackDescription
      ),
      content: (
        <Flex direction="column" gap={16}>
          <Body size="medium">
            The labels can be used as filters in the tool.
          </Body>
          <LabelListEdit
            disabled={creating}
            labels={labelListInput}
            onChange={(labels: string[]) => setLabelListInput(labels)}
          />
          <Button type="primary" onClick={nextStep}>
            Next
          </Button>
        </Flex>
      ),
      invalid: false,
    },
    {
      key: 'schema',
      title: 'Select schema',
      subTitle: '',
      description: selectedSchemaName ? (
        <Body size="medium" strong>
          {selectedSchemaName}
        </Body>
      ) : (
        fallbackDescription
      ),
      content: loadingSchemas ? (
        <Flex direction="column" gap={16} alignItems="center">
          <Spinner />
          <Body size="small">Loading schemas...</Body>
        </Flex>
      ) : (
        <Flex direction="column" gap={16}>
          <Body size="medium">
            Select the schema that contains the data you want to validate.
          </Body>
          <SelectWrapper>
            <Select
              fullWidth
              closeMenuOnSelect
              value={
                selectedSchemaName
                  ? { label: selectedSchemaName, value: selectedSchemaName }
                  : undefined
              }
              placeholder="Select schema"
              onChange={(option: OptionType<string>) =>
                handleSchemaChange(option?.value ?? '')
              }
              options={schemas.map((s) => ({
                label: s.schemaCollection.name,
                value: s.schemaCollection.name,
              }))}
            />
          </SelectWrapper>
          {schemas.length === 0 && (
            <HintText size="small">
              No schemas available in this digital twin.
            </HintText>
          )}
          <Button type="primary" disabled={hasInvalidSchema} onClick={nextStep}>
            Next
          </Button>
        </Flex>
      ),
      invalid: hasInvalidSchema,
    },
    {
      key: 'schema-type',
      title: 'Select schema type',
      subTitle: '',
      description: selectedSchemaType ? (
        <Body size="medium" strong>
          {selectedSchemaType}
        </Body>
      ) : (
        fallbackDescription
      ),
      content: renderSchemaTypeContent({
        selectedSchema,
        selectedSchemaType,
        setSelectedSchemaType,
        hasInvalidSchemaType,
        nextStep,
      }),
      invalid: hasInvalidSchemaType,
    },
    {
      key: 'filters',
      title: 'Filters (optional)',
      subTitle: '',
      description: (
        <>
          {hasFilters ? (
            <>
              <Body size="medium" strong>
                Filters defined
              </Body>
              <Body size="small">
                {filtersTree.condition.toUpperCase()} logic with nested
                conditions
              </Body>
            </>
          ) : (
            <Body size="medium">All instances (no filters)</Body>
          )}
        </>
      ),
      content: renderFiltersContent({
        filtersTree,
        setFiltersTree,
        selectedSchema,
        selectedSchemaType,
      }),
      invalid: false,
    },
  ];
};
